import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { generatePath, useParams } from 'react-router-dom'

import { Alert, message, Modal, Spin, Tooltip, Upload } from 'antd'
import type { UploadProps } from 'antd'
import {
  InboxOutlined,
  DownloadOutlined,
  LoadingOutlined
} from '@ant-design/icons'
import styled from 'styled-components'

import { colours, sizes } from '@qflow/theme'

import { ColourButton, icons, Spacer, Spinner } from 'portal/lib/primitives'
import { WASTE_EVENTS } from 'portal/routes'

import {
  ProjectsData,
  TitleBar,
  RightSide,
  ContentWrapper,
  ExtraInfo
} from './SettingsStyledComponents'
import { trackMixpanel } from 'portal/store/mixpanel/actions'
import {
  wmrConfigLoad,
  wmrRulesetUpload,
  wmrRulesetConfirm,
  wmrRulesetExport,
  wmrRulesetUploadReset,
  wmrRulesetExportReset
} from 'portal/store/wmrConfig/actions'
import { useWmrConfigLoader } from './useWmrConfigLoader'

// Constants
const ACCEPTED_FILE_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
]

const MIXPANEL_EVENTS = {
  TEMPLATE_DOWNLOAD_CLICK: 'frontend_wmr_template_download_click',
  TEMPLATE_DOWNLOAD_SUCCESS: 'frontend_wmr_template_download_success',
  TEMPLATE_DOWNLOAD_FAILURE: 'frontend_wmr_template_download_failure',
  EXPORT_CLICK: 'frontend_wmr_export_click',
  EXPORT_DOWNLOAD_SUCCESS: 'frontend_wmr_export_download_success',
  EXPORT_FAILURE: 'frontend_wmr_export_failure',
  UPLOAD_INITIATED: 'frontend_wmr_upload_initiated',
  UPLOAD_SUCCESS: 'frontend_wmr_upload_success',
  UPLOAD_FAILURE: 'frontend_wmr_upload_failure',
  CONFIRM_CLICK: 'frontend_wmr_confirm_click',
  CONFIRM_SUCCESS: 'frontend_wmr_confirm_success',
  CONFIRM_FAILURE: 'frontend_wmr_confirm_failure'
} as const

export const WmrConfig = ({ project }: WmrConfigProps) => {
  const dispatch = useDispatch()
  const { accountId } = useParams<{
    accountId: string
  }>()

  const {
    loading,
    error,
    data: wmrConfig,
    upload,
    confirm,
    exportState
  } = useWmrConfigLoader(accountId || '', project.projectId)

  // State for validation modal
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false)
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(
    null
  )
  const [lastProcessedUpload, setLastProcessedUpload] = useState<string>('')

  // State for upload modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)

  // Ref to track if confirm was initiated
  const confirmInitiatedRef = useRef(false)

  // Show error message when config fails to load
  useEffect(() => {
    if (error) {
      message.error('Failed to load Waste Routes configuration')
    }
  }, [error])

  const handleDownloadTemplate = async () => {
    try {
      if (!wmrConfig?.templateSasUrl) {
        message.error('Template URL not available')
        return
      }

      // Track template download initiation
      dispatch(
        trackMixpanel(MIXPANEL_EVENTS.TEMPLATE_DOWNLOAD_CLICK, {
          account_id: accountId,
          project_id: project.projectId
        })
      )

      // Download the template file using the SAS URL
      const link = document.createElement('a')
      link.href = wmrConfig.templateSasUrl
      link.download = 'WMR_Template.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Track successful template download
      dispatch(
        trackMixpanel(MIXPANEL_EVENTS.TEMPLATE_DOWNLOAD_SUCCESS, {
          account_id: accountId,
          project_id: project.projectId
        })
      )
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Template download failed'

      dispatch(
        trackMixpanel(MIXPANEL_EVENTS.TEMPLATE_DOWNLOAD_FAILURE, {
          account_id: accountId,
          project_id: project.projectId,
          error_message: errorMessage
        })
      )

      message.error('Failed to download template')
    }
  }

  const handleExportCurrent = () => {
    if (accountId && project.projectId) {
      dispatch(wmrRulesetExport(accountId, project.projectId))

      dispatch(
        trackMixpanel(MIXPANEL_EVENTS.EXPORT_CLICK, {
          account_id: accountId,
          project_id: project.projectId
        })
      )
    }
  }

  // Handle export success - download the file
  useEffect(() => {
    if (exportState.exportFileSasUrl) {
      const link = document.createElement('a')
      link.href = exportState.exportFileSasUrl
      link.download = 'Current_WMR_Ruleset.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      dispatch(
        trackMixpanel(MIXPANEL_EVENTS.EXPORT_DOWNLOAD_SUCCESS, {
          account_id: accountId,
          project_id: project.projectId,
          rule_count: wmrConfig?.activeRuleset?.ruleCount || 0
        })
      )

      // Reset export state after download is triggered
      dispatch(wmrRulesetExportReset())
    }
  }, [exportState, dispatch, accountId, project.projectId, wmrConfig])

  // Handle export error
  useEffect(() => {
    if (exportState.error) {
      dispatch(
        trackMixpanel(MIXPANEL_EVENTS.EXPORT_FAILURE, {
          account_id: accountId,
          project_id: project.projectId,
          error_message:
            exportState.error.message || 'Failed to export configuration'
        })
      )

      message.error('Failed to export current configuration')
    }
  }, [exportState.error, dispatch, accountId, project.projectId])

  const fileUpload = useCallback(
    async (options: any) => {
      const { file, onSuccess, onError } = options

      if (accountId && project.projectId) {
        dispatch(wmrRulesetUpload(accountId, project.projectId, file))
        onSuccess(file)
      } else {
        onError(new Error('Missing account or project ID'))
      }
    },
    [dispatch, accountId, project.projectId]
  )

  // Handle upload success - open validation modal
  useEffect(() => {
    // Create a unique identifier for this upload attempt
    const uploadKey = `${upload.ruleCount}-${upload.isValid}-${upload.rulesetId}-${upload.validationResults.length}`

    // Open validation modal when upload completes and we haven't processed this result yet
    // Show modal if we have a rulesetId (success) or if there are validation results (errors)
    if (
      !upload.loading &&
      !upload.error &&
      (upload.rulesetId !== null || upload.validationResults.length > 0) &&
      uploadKey !== lastProcessedUpload
    ) {
      dispatch(
        trackMixpanel(MIXPANEL_EVENTS.UPLOAD_SUCCESS, {
          account_id: accountId,
          project_id: project.projectId,
          ruleset_id: upload.rulesetId,
          is_valid: upload.isValid,
          rule_count: upload.ruleCount
        })
      )

      // Store upload response data
      setUploadResponse({
        rulesetId: upload.rulesetId,
        ruleCount: upload.ruleCount,
        isValid: upload.isValid,
        validationResults: upload.validationResults
      })

      // Mark this upload as processed
      setLastProcessedUpload(uploadKey)

      // Close upload modal if it's still open
      setIsUploadModalOpen(false)

      // Open validation modal
      setIsValidationModalOpen(true)
    }
  }, [
    upload.loading,
    upload.error,
    upload.rulesetId,
    upload.ruleCount,
    upload.isValid,
    upload.validationResults,
    lastProcessedUpload,
    dispatch,
    accountId,
    project.projectId
  ])

  // Handle upload error
  useEffect(() => {
    if (upload.error) {
      dispatch(
        trackMixpanel(MIXPANEL_EVENTS.UPLOAD_FAILURE, {
          account_id: accountId,
          project_id: project.projectId,
          error_message: upload.error.message || 'File upload failed'
        })
      )

      message.error(upload.error.message || 'File upload failed', 10)
    }
  }, [upload.error, dispatch, accountId, project.projectId])

  const confirmRuleset = (rulesetId: string) => {
    if (accountId && project.projectId) {
      dispatch(wmrRulesetConfirm(accountId, project.projectId, rulesetId))

      dispatch(
        trackMixpanel(MIXPANEL_EVENTS.CONFIRM_CLICK, {
          account_id: accountId,
          project_id: project.projectId,
          ruleset_id: rulesetId
        })
      )
    }
  }

  // Handle confirm success - reload config
  useEffect(() => {
    if (
      confirmInitiatedRef.current && // Only run if confirm was actually initiated
      confirm.loading === false &&
      !confirm.error &&
      accountId &&
      project.projectId &&
      isValidationModalOpen && // Only close if modal is actually open
      uploadResponse?.rulesetId // And we have an upload response
    ) {
      dispatch(
        trackMixpanel(MIXPANEL_EVENTS.CONFIRM_SUCCESS, {
          account_id: accountId,
          project_id: project.projectId
        })
      )

      // Reload the config to get updated rule count
      dispatch(wmrConfigLoad(accountId, project.projectId))

      // Close validation modal and reset state after successful confirm
      setIsValidationModalOpen(false)
      setUploadResponse(null)
      setLastProcessedUpload('')
      dispatch(wmrRulesetUploadReset())
      confirmInitiatedRef.current = false // Reset the flag
    }
  }, [
    confirm.loading,
    confirm.error,
    dispatch,
    accountId,
    project.projectId,
    isValidationModalOpen,
    uploadResponse
  ])

  // Handle confirm error
  useEffect(() => {
    if (confirm.error) {
      dispatch(
        trackMixpanel(MIXPANEL_EVENTS.CONFIRM_FAILURE, {
          account_id: accountId,
          project_id: project.projectId,
          error_message: confirm.error.message || 'Failed to confirm ruleset'
        })
      )

      message.error('Failed to confirm ruleset')
    }
  }, [confirm.error, dispatch, accountId, project.projectId])

  const { Dragger } = Upload

  const draggerProps: UploadProps = {
    name: 'file',
    accept: '.xlsx',
    multiple: false,
    maxCount: 1,
    customRequest: fileUpload,
    beforeUpload: file => {
      // Check if the file type is allowed
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        message.error('You can only upload XLSX files.')
        return Upload.LIST_IGNORE
      }
      return true
    },
    onChange(info) {
      const { status } = info.file
      if (status === 'uploading') {
        // Track upload initiation
        dispatch(
          trackMixpanel(MIXPANEL_EVENTS.UPLOAD_INITIATED, {
            account_id: accountId,
            project_id: project.projectId,
            file_name: info.file.name,
            file_size: info.file.size,
            file_type: info.file.type
          })
        )
      }
    }
  }

  const handleUploadFile = () => {
    setIsUploadModalOpen(true)
  }

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false)
  }

  const handleConfirmRuleset = () => {
    if (uploadResponse?.rulesetId) {
      confirmInitiatedRef.current = true
      confirmRuleset(uploadResponse.rulesetId)
      // Don't close modal here - let it stay open to show "Confirming..." state
      // Modal will close when confirm succeeds (in the useEffect below)
    }
  }

  const handleCloseValidationModal = () => {
    setIsValidationModalOpen(false)
    setUploadResponse(null)
    setLastProcessedUpload('')
    dispatch(wmrRulesetUploadReset())
  }

  if (loading) {
    return <Spinner />
  }

  return (
    <>
      <Modal
        title="Upload Waste Route Configuration"
        open={isUploadModalOpen}
        onCancel={handleCloseUploadModal}
        width={600}
        footer={null}
      >
        <ContentWrapper>
          <Spin
            spinning={upload.loading}
            tip="Uploading and validating file..."
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            style={{
              color: colours.CORE_NAVY,
              background: `${colours.PALE_GREY}a8`,
              borderRadius: '0.5rem'
            }}
          >
            <Dragger {...draggerProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: colours.TEAL }} />
              </p>
              <p className="ant-upload-text">
                Click or drag and drop your file here
              </p>
            </Dragger>
            <ExtraInfo>
              Supported formats: XLSX. Upload your Waste Route Configuration
              file.
            </ExtraInfo>
          </Spin>
        </ContentWrapper>
      </Modal>

      <Modal
        title="Configuration Validation Results"
        open={isValidationModalOpen}
        onCancel={handleCloseValidationModal}
        width={700}
        footer={[
          <ColourButton.Basic key="close" onClick={handleCloseValidationModal}>
            Cancel
          </ColourButton.Basic>,
          uploadResponse?.isValid && uploadResponse?.rulesetId && (
            <ColourButton.Action
              key="confirm"
              onClick={handleConfirmRuleset}
              disabled={confirm.loading}
            >
              {confirm.loading ? (
                <>
                  <LoadingOutlined /> Confirming...
                </>
              ) : (
                'Confirm Configuration'
              )}
            </ColourButton.Action>
          )
        ]}
        styles={{
          body: { maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' },
          footer: { display: 'flex', justifyContent: 'flex-end', gap: '8px' }
        }}
      >
        <ValidationContent>
          {uploadResponse && (
            <>
              <ValidationSummary>
                <div>
                  <StatusLabel>Status:</StatusLabel>
                  <StatusBadge isValid={uploadResponse.isValid}>
                    {uploadResponse.isValid ? 'Valid' : 'Invalid'}
                  </StatusBadge>
                </div>
                <div>
                  <StatusLabel>Number of Routes:</StatusLabel>
                  <span>
                    {uploadResponse.ruleCount}
                    {wmrConfig?.activeRuleset?.ruleCount ? (
                      <i> (previously {wmrConfig.activeRuleset.ruleCount})</i>
                    ) : (
                      ''
                    )}
                  </span>
                </div>
              </ValidationSummary>
              {uploadResponse.isValid && uploadResponse.ruleCount > 0 && (
                <>
                  <Alert
                    type="success"
                    message="File uploaded successfully"
                    description='Click "Confirm Configuration" to apply the changes.'
                    showIcon
                  />

                  {/* If there is a current config with rules, and the new rule count is greater than 0, show a warning about replacing these rules with the new config. */}
                  {wmrConfig?.activeRuleset &&
                    wmrConfig.activeRuleset.ruleCount > 0 && (
                      <Alert
                        type="warning"
                        message="Existing configuration will be replaced"
                        description={`Confirming will replace the existing ${wmrConfig.activeRuleset.ruleCount} routes, with the ${uploadResponse.ruleCount} routes in the uploaded file.`}
                        showIcon
                        style={{ marginTop: '1rem' }}
                      />
                    )}
                </>
              )}
              {uploadResponse.isValid && uploadResponse.ruleCount === 0 && (
                <Alert
                  type="warning"
                  message="No routes found"
                  description='The uploaded file contains no valid routes. Click "Confirm Configuration" to clear any existing routes, or upload a new file with routes configured.'
                  showIcon
                />
              )}

              {!uploadResponse.isValid && (
                <Alert
                  type="error"
                  message="Configuration contains errors"
                  description="Please correct the errors in your file and re-upload."
                  showIcon
                  style={{ marginBottom: '1rem' }}
                />
              )}
              {uploadResponse.validationResults.length > 0 && (
                <ValidationResults>
                  <h4>Validation Details</h4>
                  {uploadResponse.validationResults.map((result, index) => {
                    const errors = result.issues.filter(issue => issue.isError)
                    const warnings = result.issues.filter(
                      issue => !issue.isError
                    )

                    return (
                      <ValidationRow key={index}>
                        <RowHeader>Row {result.rowNumber}</RowHeader>
                        {errors.length > 0 && (
                          <ErrorSection>
                            {errors.map((error, errIdx) => (
                              <ErrorItem key={errIdx}>
                                <ErrorLabel>
                                  Error in {error.column}:
                                </ErrorLabel>
                                <ErrorMessage>{error.message}</ErrorMessage>
                              </ErrorItem>
                            ))}
                          </ErrorSection>
                        )}
                        {warnings.length > 0 && (
                          <WarningSection>
                            {warnings.map((warning, warnIdx) => (
                              <WarningItem key={warnIdx}>
                                <WarningLabel>
                                  Warning in {warning.column}:
                                </WarningLabel>
                                <WarningMessage>
                                  {warning.message}
                                </WarningMessage>
                              </WarningItem>
                            ))}
                          </WarningSection>
                        )}
                      </ValidationRow>
                    )
                  })}
                </ValidationResults>
              )}
            </>
          )}
        </ValidationContent>
      </Modal>

      <ProjectsData>
        <TitleBar>
          <h3>{project.name}</h3>
          <RightSide>
            {wmrConfig &&
              wmrConfig.activeRuleset &&
              wmrConfig.activeRuleset.ruleCount > 0 && (
                <ColourButton.Trigger
                  onClick={handleExportCurrent}
                  disabled={exportState.loading}
                >
                  <DownloadOutlined />
                  <Spacer.Fine />
                  {exportState.loading
                    ? 'Exporting...'
                    : 'Export Configuration'}
                </ColourButton.Trigger>
              )}
            {wmrConfig &&
              (!wmrConfig.activeRuleset ||
                wmrConfig.activeRuleset.ruleCount === 0) &&
              wmrConfig.templateSasUrl && (
                <ColourButton.Trigger onClick={handleDownloadTemplate}>
                  <DownloadOutlined />
                  <Spacer.Fine />
                  Download Template
                </ColourButton.Trigger>
              )}

            <Tooltip
              title={
                wmrConfig?.activeRuleset?.isReprocessing ? (
                  <span>
                    An import is currently being processed. No new
                    configurations can be imported until this has completed.
                    Check progress on the{' '}
                    <TooltipLink
                      href={generatePath(WASTE_EVENTS, {
                        accountId: accountId ?? '',
                        projectId: project.projectId
                      })}
                    >
                      Waste Events
                    </TooltipLink>{' '}
                    page.
                  </span>
                ) : null
              }
            >
              <span>
                <ColourButton.Trigger
                  onClick={handleUploadFile}
                  disabled={wmrConfig?.activeRuleset?.isReprocessing}
                >
                  <icons.Upload size={sizes.SMALL} />
                  <Spacer.Fine />
                  Import Configuration
                </ColourButton.Trigger>
              </span>
            </Tooltip>
          </RightSide>
        </TitleBar>

        <Section>
          <h4>Waste Management Routes Configuration</h4>
          <Wrapper>
            <InfoWrapper>
              <InfoText>
                By default, waste management routes are derived from
                facility-reported data submitted to environmental regulators
                (e.g. EA, SEPA, NRW). You can define custom routes that will be
                applied in preference to facility-reported data for this
                project.
              </InfoText>
              <Alert
                type="warning"
                message="Important: Routes will be applied to all records for the specified year."
                showIcon
              />
            </InfoWrapper>
            {wmrConfig &&
              wmrConfig.activeRuleset &&
              wmrConfig.activeRuleset.ruleCount > 0 && (
                <ConfigWrapper>
                  <ConfigStatus>
                    <StatusLabel>Current Configuration:</StatusLabel>
                    <StatusValue>
                      <RuleCountNumber>
                        {wmrConfig.activeRuleset.ruleCount}
                      </RuleCountNumber>{' '}
                      <div>
                        custom route
                        {wmrConfig.activeRuleset.ruleCount !== 1
                          ? 's'
                          : ''}{' '}
                        configured
                      </div>
                    </StatusValue>
                  </ConfigStatus>
                </ConfigWrapper>
              )}

            {wmrConfig &&
              (!wmrConfig.activeRuleset ||
                wmrConfig.activeRuleset.ruleCount === 0) &&
              wmrConfig.templateSasUrl && (
                <ConfigWrapper>
                  <ConfigStatus>
                    <StatusLabel>Current Configuration:</StatusLabel>
                    <StatusValue>No custom routes configured</StatusValue>
                  </ConfigStatus>
                </ConfigWrapper>
              )}
          </Wrapper>

          {wmrConfig &&
            wmrConfig.activeRuleset &&
            wmrConfig.activeRuleset.ruleCount > 0 && (
              <Alert
                type="info"
                message="Adding or removing routes"
                description="To update your configuration, download a copy of your existing configuration using the Export button above, make your changes, and re-import it. You can add new rows, edit existing ones, or remove rows that no longer apply. Any changes will be reflected on affected tickets upon reprocessing, including the removal of routes that have been deleted or overwritten."
                showIcon
                style={{ marginTop: '1rem' }}
              />
            )}
        </Section>

        <Section>
          <GuideContent>
            <h4>How to configure custom routes</h4>
            <ol>
              <li>
                {wmrConfig &&
                wmrConfig.activeRuleset &&
                wmrConfig.activeRuleset.ruleCount > 0 ? (
                  <b>Export your existing configuration</b>
                ) : (
                  <b>Download the template</b>
                )}{' '}
                using the button above and open it in Excel or a compatible
                spreadsheet application.
              </li>
              <li>
                <b>Fill in your custom routes</b>. Each row represents one
                route. For each route, specify:
                <ul>
                  <li>
                    <b>Facility Permit ID:</b> enter the Facility ID the route
                    applies to
                  </li>
                  <li>
                    <b>Year:</b> enter the year from which the route should
                    apply
                  </li>
                  <li>
                    <b>Waste Code (EWC):</b> enter a specific EWC code, or leave
                    blank to apply the route to all EWC codes for that facility
                  </li>
                  <li>
                    <b>Exemption:</b> if this Facility ID is an exemption, enter
                    Yes in the Exemption column. Leave waste route percentages
                    blank for exempted facilities
                  </li>
                  <li>
                    <b>Waste Management Route - Percentages:</b> enter the waste
                    route percentages for this route, e.g. 80% Recycling, 20%
                    Reuse. All route percentages must add up to 100%
                  </li>
                </ul>
              </li>
              <li>
                <b>Save and upload:</b> Save and upload your file using the
                Import button above.
              </li>
              <li>
                <b>Review validation results:</b> The system will check your
                file before applying any changes. Any unrecognised EWC codes
                will be flagged and these rows will not be applied until
                corrected.
              </li>
              <li>
                <b>Confirm to apply:</b> Once you are happy with the validated
                results, confirm to save your configuration.
              </li>
            </ol>

            <h5>Things to know</h5>
            <ul>
              <li>
                Custom routes take priority over facility-reported data for any
                matching records
              </li>
              <li>
                Custom routes apply to all records from 1st January onwards for
                the specified year
              </li>
              <li>
                You can re-upload at any time to update your configuration
              </li>
              <li>
                Where no custom route applies, routes will continue to be
                derived from regulatory data
              </li>
            </ul>
          </GuideContent>
        </Section>
      </ProjectsData>
    </>
  )
}

type WmrConfigProps = {
  project: ProjectSettings
}

export interface ProjectSettings {
  name: string
  projectId: string
  maxAlertRules: number
  isOneClickLcaEnabled: boolean
  isEC3Enabled: boolean
}

interface ValidationIssue {
  column: string
  message: string
  isError: boolean
}

interface ValidationResult {
  rowNumber: number
  issues: ValidationIssue[]
}

interface UploadResponse {
  rulesetId: string | null
  ruleCount: number
  isValid: boolean
  validationResults: ValidationResult[]
}

const Section = styled.div`
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 0.625rem;
  padding: 1rem 1.5rem;
  margin-bottom: 0.625rem;

  & > h4 {
    color: ${colours.CORE_NAVY};
    font-family: 'Jakarta Sans', 'Inter', sans-serif !important;
    font-size: 0.9375rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }
`
const Wrapper = styled.div`
  display: flex;
  gap: 1rem;
  align-items: stretch;
`
const InfoWrapper = styled.div`
  flex: 3;
`
const InfoText = styled.div`
  font-size: 0.875rem;
  margin: 0.5rem 0;
  width: 100%;

  & p:last-child {
    margin: 0.5rem 0 0 0;
  }
`
const ConfigWrapper = styled.div`
  padding: 0.75rem;
  background-color: ${colours.WHITE};
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  box-shadow: 0px 1px 3px 0px #00000026;
`
const ConfigStatus = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
`
const StatusLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${colours.CORE_NAVY};
`
const StatusValue = styled.div`
  font-size: 0.875rem;
  text-align: center;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
`
const RuleCountNumber = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
`
const TooltipLink = styled.a`
  color: ${colours.TEAL};
`
const GuideContent = styled.div`
  & > h4 {
    color: ${colours.CORE_NAVY};
    font-family: 'Jakarta Sans', 'Inter', sans-serif !important;
    font-size: 0.9375rem;
    font-weight: 700;
    margin-bottom: 0.75rem;
  }

  h5 {
    color: ${colours.CORE_NAVY};
    font-size: 0.9375rem;
    font-weight: 600;
    margin-bottom: 1rem;
    margin: 1.5rem 0 0.75rem 0;
  }

  p {
    line-height: 1.6;
    margin-bottom: 1rem;
  }

  ol,
  ul {
    margin-left: 1rem;
    line-height: 1.8;

    li {
      margin-bottom: 0.5rem;

      strong {
        color: ${colours.CORE_NAVY};
      }

      ul {
        margin-top: 0.5rem;
        margin-left: 1.5rem;
        list-style-type: disc;

        li {
          margin-bottom: 0.25rem;
        }
      }
    }
  }

  ul {
    list-style-type: disc;
  }
`
const ValidationContent = styled.div`
  margin: 0 0 1rem 0;
`
const ValidationSummary = styled.div`
  display: flex;
  gap: 2rem;
  padding: 0.75rem 1rem;
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 0.5rem;
  margin-bottom: 1rem;

  div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`
const StatusBadge = styled.span<{ isValid: boolean }>`
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-weight: 500;
  font-size: 0.875rem;
  background-color: ${props =>
    props.isValid ? colours.WHITE : colours.ERROR_BACKGROUND};
  color: ${props => (props.isValid ? colours.SUCCESS : colours.ERROR)};
`
const ValidationResults = styled.div`
  h4 {
    font-size: 0.9375rem;
    font-weight: 600;
    margin: 1rem 0;
    color: ${colours.CORE_NAVY};
  }
`
const ValidationRow = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 0.5rem;
`
const RowHeader = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
  color: ${colours.CORE_NAVY};
`
const ErrorSection = styled.div`
  margin-bottom: 0.75rem;
`
const ErrorItem = styled.div`
  padding: 0.5rem;
  background-color: ${colours.ERROR_BACKGROUND};
  border-left: 3px solid ${colours.ERROR};
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
`
const ErrorLabel = styled.div`
  font-weight: 600;
  font-size: 0.8125rem;
  color: ${colours.ERROR};
  margin-bottom: 0.25rem;
`
const ErrorMessage = styled.div`
  font-size: 0.8125rem;
  color: ${colours.CORE_NAVY};
`
const WarningSection = styled.div`
  margin-bottom: 0.75rem;
`
const WarningItem = styled.div`
  padding: 0.5rem;
  background-color: ${colours.WARNING_BACKGROUND};
  border-left: 3px solid ${colours.WARNING};
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
`
const WarningLabel = styled.div`
  font-weight: 600;
  font-size: 0.8125rem;
  color: ${colours.ORANGE};
  margin-bottom: 0.25rem;
`
const WarningMessage = styled.div`
  font-size: 0.8125rem;
  color: ${colours.CORE_NAVY};
`
